import React, { useState, useEffect, useRef } from "react";
import { parseLineQuantity, isJobFrozen } from "./jobMath";

const LINE_STATUS_LABEL = { ok: null, missing: "Takeoff missing", incomplete: "Incomplete" };

export default function JobScreen({
  job, lineDetails, unitTypeTemplates, customTemplate, canEdit, markupError,
  onChangeField, onChangeMarkup, onAddLine, onOpenLine, onChangeQuantity, onSave, onBack, onMarkSent, onRevise, busyAction,
  onGenerateProposal, onDownloadSentProposal, onChangeLineDescription,
  vocabulary = {}, // threaded for client vocabulary; no module noun rendered here yet
}) {
  const rollup = lineDetails?.rollup || { incomplete: true, subtotal: null, markup: null, total: null };
  const lines = lineDetails?.lines || [];
  // Task 6: a sent job is frozen. Every edit control is disabled here and
  // App.js's handlers refuse the edit too; totals come from job.frozen.
  const frozen = isJobFrozen(job);
  const sendBlocked = rollup.incomplete || !!markupError || !!busyAction;
  // Fix round 1: disabled while a Mark Sent or Revise turn is in flight.
  const reviseBlocked = !!busyAction;
  // Task 7: the draft proposal preview needs a complete rollup, like Mark
  // Sent, and (fix round 1) waits while Mark Sent or Revise is in flight.
  const previewBlocked = rollup.incomplete || !!markupError || !!busyAction;
  // Fix round 2: the tooltip names the actual reason.
  const previewBlockedReason = busyAction
    ? "Wait for the current action to finish"
    : markupError
      ? "The markup is invalid"
      : rollup.incomplete
        ? "One or more lines is incomplete or missing its takeoff"
        : "";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <button onClick={onBack} style={{ background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 6, color: "var(--rf-text-soft)", fontSize: 12, padding: "6px 12px", cursor: "pointer" }}>
          ← Back
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          {canEdit && !frozen && (
            <button onClick={onSave} style={{ background: "var(--rf-primary)", border: "none", borderRadius: 7, color: "white", fontSize: 13, fontWeight: 600, padding: "8px 16px", cursor: "pointer" }}>
              Save
            </button>
          )}
          {canEdit && !frozen && (
            <button onClick={onMarkSent} disabled={sendBlocked} title={sendBlocked ? "One or more lines is incomplete, or the markup is invalid" : ""} style={{ background: "#16a34a", border: "none", borderRadius: 7, color: "white", fontSize: 13, fontWeight: 600, padding: "8px 16px", cursor: sendBlocked ? "not-allowed" : "pointer", opacity: sendBlocked ? 0.5 : 1 }}>
              {busyAction === "markSent" ? "Marking Sent..." : "Mark Sent"}
            </button>
          )}
          {canEdit && frozen && (
            <button onClick={onRevise} disabled={reviseBlocked} style={{ background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-accent)", fontSize: 13, fontWeight: 600, padding: "8px 16px", cursor: reviseBlocked ? "not-allowed" : "pointer", opacity: reviseBlocked ? 0.5 : 1 }}>
              {busyAction === "revise" ? "Revising..." : "Revise"}
            </button>
          )}
        </div>
      </div>

      {frozen && (
        <div style={{ marginBottom: 16, padding: "10px 14px", border: "1px solid var(--rf-border)", borderRadius: 7, background: "var(--rf-bg)", color: "var(--rf-text-soft)", fontSize: 13 }}>
          {rollup.frozenError
            ? rollup.frozenError
            : `Sent. These figures are frozen and will not change, even if rates or takeoffs change.${canEdit ? " Use Revise to make an editable copy." : ""}`}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
        <Field label="Name" value={job.name} resetKey={job.id} onCommit={(v) => onChangeField("name", v)} disabled={!canEdit || frozen} />
        <Field label="Property" value={job.property} resetKey={job.id} onCommit={(v) => onChangeField("property", v)} disabled={!canEdit || frozen} />
        <Field label="Address" value={job.address} resetKey={job.id} onCommit={(v) => onChangeField("address", v)} disabled={!canEdit || frozen} />
        <Field label="Contact" value={job.contact} resetKey={job.id} onCommit={(v) => onChangeField("contact", v)} disabled={!canEdit || frozen} />
        <div>
          <div style={{ fontSize: 10, color: "var(--rf-muted)", marginBottom: 3, textTransform: "uppercase" }}>Status</div>
          <div style={{ fontSize: 14, color: "var(--rf-text)", padding: "6px 0" }}>{job.status || "draft"}{(job.revision || 1) > 1 ? ` (Rev ${job.revision})` : ""}</div>
        </div>
        <MarkupInput job={job} disabled={!canEdit || frozen} markupError={markupError} onChangeMarkup={onChangeMarkup} />
      </div>

      {canEdit && !frozen && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {(unitTypeTemplates || []).map((t) => (
            <button key={t.id} onClick={() => onAddLine("unit_type", t)} style={{ background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-accent)", fontSize: 13, padding: "8px 14px", cursor: "pointer" }}>
              + Add {t.name}
            </button>
          ))}
          {customTemplate && (
            <button onClick={() => onAddLine("custom", customTemplate)} style={{ background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-accent)", fontSize: 13, padding: "8px 14px", cursor: "pointer" }}>
              + Add custom area
            </button>
          )}
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 16 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--rf-border)", textAlign: "left", color: "var(--rf-muted)", fontSize: 11, textTransform: "uppercase" }}>
            <th style={{ padding: "8px 6px" }}>Description</th>
            <th style={{ padding: "8px 6px" }}>Quantity</th>
            <th style={{ padding: "8px 6px", textAlign: "right" }}>Subtotal each</th>
            <th style={{ padding: "8px 6px", textAlign: "right" }}>Line subtotal</th>
            <th style={{ padding: "8px 6px" }} />
          </tr>
        </thead>
        <tbody>
          {lines.map((ld) => {
            const badge = LINE_STATUS_LABEL[ld.status];
            // Fix round 1, finding #4: mirrors the Reports tab's own rule
            // (canEditReports && !r._submitted) for whether "Open" is even
            // offered — a base user, or anyone once the underlying takeoff
            // has been submitted, never gets an editable path into it from
            // here, same as they never would from the Reports tab itself.
            // Task 6: a sent job's takeoffs are offered to anyone who can see
            // the job, as "View", and App.js opens them read-only.
            const canOpenLine = frozen || (canEdit && !ld.submitted);
            return (
              <tr key={ld.line.id} style={{ borderBottom: "1px solid var(--rf-surface)" }}>
                <td style={{ padding: "10px 6px", color: "var(--rf-text-heading)" }}>
                  {/* Task 7 fix round 1 (R22): the description is what the
                      customer reads on the proposal, so it is editable on a
                      draft and locked once sent. */}
                  {canEdit && !frozen ? (
                    <LineDescriptionInput line={ld.line} onCommit={(v) => onChangeLineDescription(ld.line.id, v)} />
                  ) : (ld.line.description || "Untitled line")}
                  {badge && (
                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "var(--rf-danger)", background: "var(--rf-bg)", border: "1px solid var(--rf-danger)", borderRadius: 4, padding: "2px 7px" }}>
                      {badge}
                    </span>
                  )}
                  {/* Final fix wave, item 1: why the line is incomplete when
                      App.js knows (a quantity mismatch or an evaluation error). */}
                  {ld.status !== "ok" && ld.error && (
                    <div style={{ marginTop: 4, fontSize: 11, color: "var(--rf-danger)", maxWidth: 520 }}>{ld.error}</div>
                  )}
                </td>
                <td style={{ padding: "10px 6px" }}>
                  {ld.line.kind === "custom" ? "1" : (
                    <QuantityInput
                      line={ld.line}
                      disabled={!canEdit || frozen}
                      onCommit={(value) => onChangeQuantity(ld.line.id, value)}
                    />
                  )}
                </td>
                <td style={{ padding: "10px 6px", textAlign: "right", color: "var(--rf-text-soft)" }}>
                  {ld.status === "ok" ? `$${(ld.lineSubtotal / (ld.line.quantity ?? 1)).toFixed(2)}` : "-"}
                </td>
                <td style={{ padding: "10px 6px", textAlign: "right", color: "var(--rf-text)" }}>
                  {ld.status === "ok" ? `$${ld.lineSubtotal.toFixed(2)}` : "-"}
                </td>
                <td style={{ padding: "10px 6px", textAlign: "right" }}>
                  {canOpenLine && (
                    <button onClick={() => onOpenLine(ld.line)} style={{ background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 6, color: "var(--rf-accent)", fontSize: 12, padding: "5px 12px", cursor: "pointer" }}>
                      {frozen ? "View" : "Open →"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 24, marginBottom: 16, fontSize: 14 }}>
        <div>Subtotal: <strong style={{ color: "var(--rf-text-heading)" }}>{rollup.subtotal != null ? `$${rollup.subtotal.toFixed(2)}` : "-"}</strong></div>
        <div>Markup: <strong style={{ color: "var(--rf-text-heading)" }}>{rollup.markup != null ? `$${rollup.markup.toFixed(2)}` : "-"}</strong></div>
        <div>Total: <strong style={{ color: "var(--rf-text-heading)", fontSize: 16 }}>{rollup.total != null ? `$${rollup.total.toFixed(2)}` : "-"}</strong></div>
      </div>

      {/* Task 7: a sent job offers the proposal stored when it was sent,
          never a regenerated one. A draft offers a live preview, disabled
          until every line is complete. */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
        {frozen && job.frozen?.proposalPath && (
          <button onClick={onDownloadSentProposal} disabled={!!busyAction} style={{ background: "transparent", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-accent)", fontSize: 13, fontWeight: 600, padding: "10px 20px", cursor: busyAction ? "not-allowed" : "pointer", opacity: busyAction ? 0.5 : 1 }}>
            {busyAction === "download" ? "Downloading..." : "Download sent proposal"}
          </button>
        )}
        {frozen && !job.frozen?.proposalPath && (
          <div style={{ fontSize: 12, color: "var(--rf-text-dim)" }}>No proposal PDF was stored when this job was sent.</div>
        )}
        {!frozen && (
          <button
            onClick={onGenerateProposal}
            disabled={previewBlocked}
            title={previewBlockedReason}
            style={{ background: previewBlocked ? "var(--rf-border)" : "var(--rf-primary)", border: "none", borderRadius: 7, color: previewBlocked ? "var(--rf-text-dim)" : "white", fontSize: 13, fontWeight: 600, padding: "10px 20px", cursor: previewBlocked ? "not-allowed" : "pointer" }}
          >
            Proposal PDF
          </button>
        )}
      </div>
    </div>
  );
}

// Fix round 4 (controller ruling R9): header text and markup are local
// drafts, committed on blur or Enter, never per keystroke. App.js applies a
// commit inside a queued turn, so nothing in flight can snap the field back.
//
// The draft re-syncs from the committed value when the job changes
// (resetKey), or when the committed value changes while the input is NOT
// focused. It never re-syncs mid-typing, so a queued commit or a save that
// lands while the user is typing can't overwrite what they're typing.
// `lastSent` is the last value this input handed to App.js, so a commit is
// skipped only when nothing new was typed since then, even if App.js hasn't
// applied the previous commit yet.
function useCommittedDraft(committedText, resetKey) {
  const [draft, setDraft] = useState(committedText);
  const focusedRef = useRef(false);
  const lastSentRef = useRef(committedText);
  const prevKeyRef = useRef(resetKey);

  useEffect(() => {
    const keyChanged = prevKeyRef.current !== resetKey;
    prevKeyRef.current = resetKey;
    if (keyChanged || !focusedRef.current) {
      lastSentRef.current = committedText;
      setDraft(committedText);
    }
  }, [resetKey, committedText]);

  // Returns the text to commit, or null when there's nothing new.
  const takeCommit = () => {
    if (draft === lastSentRef.current) return null;
    lastSentRef.current = draft;
    return draft;
  };

  // Task 7 fix round 2: put the draft back to the committed value (used when
  // a draft is refused, so the screen never shows text the job doesn't hold).
  const revert = () => {
    lastSentRef.current = committedText;
    setDraft(committedText);
  };

  const inputProps = {
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onFocus: () => { focusedRef.current = true; },
  };
  return { draft, takeCommit, revert, focusedRef, inputProps };
}

function Field({ label, value, resetKey, onCommit, disabled }) {
  const { takeCommit, focusedRef, inputProps } = useCommittedDraft(value || "", resetKey);
  const commit = () => {
    const text = takeCommit();
    if (text !== null) onCommit(text);
  };
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--rf-muted)", marginBottom: 3, textTransform: "uppercase" }}>{label}</div>
      <input
        {...inputProps}
        disabled={disabled}
        onBlur={() => { focusedRef.current = false; commit(); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text)", fontSize: 14, padding: "6px 10px", boxSizing: "border-box" }}
      />
    </div>
  );
}

// Task 7 fix round 1 (ruling R22): a line's description, as a local draft
// committed on blur/Enter like the header fields (useCommittedDraft). An
// empty draft is never committed. Fix round 2: it is put back to the
// committed description, with a brief inline error, so the screen never
// shows a blank while the proposal would print the old text.
function LineDescriptionInput({ line, onCommit }) {
  const { draft, takeCommit, revert, focusedRef, inputProps } = useCommittedDraft(line.description || "", line.id);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);
  const commit = () => {
    if (!draft.trim()) {
      revert();
      setError("A description is required, so the previous one was kept. The customer sees it on the proposal.");
      return;
    }
    setError(null);
    const text = takeCommit();
    if (text !== null) onCommit(text);
  };
  return (
    <div style={{ display: "inline-block", minWidth: 220 }}>
      <input
        {...inputProps}
        aria-label="Line description"
        onBlur={() => { focusedRef.current = false; commit(); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 6, color: "var(--rf-text-heading)", fontSize: 13, padding: "4px 8px", boxSizing: "border-box" }}
      />
      {error && <div style={{ color: "var(--rf-danger)", fontSize: 11, marginTop: 2 }}>{error}</div>}
    </div>
  );
}

// Fix round 1, finding #6: the input shows this component's own draft
// text, never job.markupPercent directly, so an in-progress value like
// "17." is never reformatted under the cursor.
//
// Fix round 4 (ruling R9): commits on blur/Enter instead of per keystroke.
// App.js's changeJobMarkup still validates with parseMarkupPercent the
// moment it's called and sets the inline error synchronously; a valid
// value is applied to the job in a queued turn.
function MarkupInput({ job, disabled, markupError, onChangeMarkup }) {
  const committed = job.markupPercent != null ? String(job.markupPercent) : "";
  const { takeCommit, focusedRef, inputProps } = useCommittedDraft(committed, job.id);
  const commit = () => {
    const text = takeCommit();
    if (text !== null) onChangeMarkup(text);
  };

  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--rf-muted)", marginBottom: 3, textTransform: "uppercase" }}>Markup %</div>
      <input
        type="number" {...inputProps} disabled={disabled}
        onBlur={() => { focusedRef.current = false; commit(); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        style={{ width: "100%", background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 7, color: "var(--rf-text)", fontSize: 14, padding: "6px 10px", boxSizing: "border-box" }}
      />
      {markupError && <div style={{ color: "var(--rf-danger)", fontSize: 11, marginTop: 4 }}>{markupError}</div>}
    </div>
  );
}

// Fix round 1, findings #1 and #3: keeps its own draft text (keyed to this
// line by the parent row's key={ld.line.id}) and only calls onCommit on
// blur or Enter. An invalid draft shows its own inline error and is never
// handed to onCommit.
//
// Fix round 4: onCommit resolves true only when App.js actually saved the
// change. On anything else (save failed, turn skipped or threw) the draft
// goes back to the committed quantity, so the input never shows "5" while
// the job still uses 1. Only resets if the user hasn't typed something new
// since the failed attempt.
function QuantityInput({ line, disabled, onCommit }) {
  const [draft, setDraft] = useState(String(line.quantity ?? 1));
  const [error, setError] = useState(null);
  const quantityRef = useRef(line.quantity);
  quantityRef.current = line.quantity;

  useEffect(() => {
    setDraft(String(line.quantity ?? 1));
    setError(null);
  }, [line.id, line.quantity]);

  const commit = () => {
    const result = parseLineQuantity(draft);
    if (!result.ok) { setError(result.error); return; }
    setError(null);
    if (result.value === line.quantity) return;
    const attempted = draft;
    Promise.resolve(onCommit(result.value)).then((ok) => {
      if (ok === true) return;
      setDraft((d) => (d === attempted ? String(quantityRef.current ?? 1) : d));
    });
  };

  return (
    <div>
      <input
        type="number" value={draft} disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
        }}
        style={{ width: 70, background: "var(--rf-bg)", border: "1px solid var(--rf-border)", borderRadius: 6, color: "var(--rf-text)", fontSize: 13, padding: "4px 8px" }}
      />
      {error && <div style={{ color: "var(--rf-danger)", fontSize: 11, marginTop: 2 }}>{error}</div>}
    </div>
  );
}
